// Portions of this file are Copyright 2021 Google LLC, and licensed under GPL2+. See COPYING.
/// <reference lib="webworker" />
import OpenSCAD from "../wasm/openscad.js";
import { fetchLibraryTree, getParentDir, LIBRARIES_ROOT } from "../fs/filesystem.ts";
import { fetchSource } from "../utils.ts";
function callback(payload) {
    self.postMessage(payload);
}
// Font files ship under /libraries/fonts but OpenSCAD looks for them at /fonts.
const FONTS_DIR = `${LIBRARIES_ROOT}/fonts`;
const mapToInstancePath = (p) => p.startsWith(`${FONTS_DIR}/`) ? '/fonts' + p.substring(FONTS_DIR.length) : p;
/** Write the fetched library tree straight into Emscripten's in-memory FS. */
function populateInstanceFS(FS, files) {
    const ensureDir = (dir) => {
        if (dir === '' || dir === '/')
            return;
        ensureDir(getParentDir(dir));
        try {
            FS.mkdir(dir);
        }
        catch (e) { /* already exists */ }
    };
    for (const [path, data] of files) {
        const target = mapToInstancePath(path);
        ensureDir(getParentDir(target));
        FS.writeFile(target, data);
    }
}
self.addEventListener('message', async (e) => {
    const { mountArchives, inputs, args, outputPaths, } = e.data;
    const mergedOutputs = [];
    let instance;
    const start = performance.now();
    try {
        instance = await OpenSCAD({
            noInitialRun: true,
            'print': (text) => {
                console.debug('stdout: ' + text);
                callback({ stdout: text });
                mergedOutputs.push({ stdout: text });
            },
            'printErr': (text) => {
                console.debug('stderr: ' + text);
                callback({ stderr: text });
                mergedOutputs.push({ stderr: text });
            },
        });
        if (mountArchives) {
            // Fetch the model tree once (cached across renders) and write it directly
            // into Emscripten's FS: library files under /libraries, fonts under /fonts.
            const { files } = await fetchLibraryTree();
            populateInstanceFS(instance.FS, files);
        }
        // Fonts are seemingly resolved from $(cwd)/fonts
        instance.FS.chdir("/");
        instance.FS.mkdir('/locale');
        // const walkFolder = (path: string, indent = '') => {
        //   console.log("Walking " + path);
        //   instance.FS.readdir(path)?.forEach((f: string) => {
        //     if (f.startsWith('.')) {
        //       return;
        //     }
        //     const ii = indent + '  ';
        //     const p = `${path != '/' ? path + '/' : '/'}${f}`;
        //     console.log(`${ii}${p}`);
        //     walkFolder(p, ii);
        //   });
        // };
        // walkFolder('/libraries');
        if (inputs) {
            for (const source of inputs) {
                try {
                    console.log(`Writing ${source.path}`);
                    if (source.content == null && source.path != null && source.url == null) {
                        if (!instance.FS.isFile(source.path)) {
                            console.error(`File ${source.path} does not exist!`);
                        }
                    }
                    else {
                        instance.FS.writeFile(source.path, await fetchSource(instance.FS, source));
                    }
                }
                catch (e) {
                    console.trace(e);
                    throw new Error(`Error while trying to write ${source.path}: ${e}`);
                }
            }
        }
        console.log('Invoking OpenSCAD with: ', args);
        let exitCode;
        try {
            exitCode = instance.callMain(args);
        }
        catch (e) {
            if (typeof e === "number" && instance.formatException) {
                // The number was a raw C++ exception
                // See https://github.com/emscripten-core/emscripten/pull/16343
                e = instance.formatException(e);
            }
            throw new Error(`OpenSCAD invocation failed: ${e}`);
        }
        const end = performance.now();
        const elapsedMillis = end - start;
        const outputs = [];
        for (const path of (outputPaths ?? [])) {
            try {
                const content = instance.FS.readFile(path);
                outputs.push([path, content]);
            }
            catch (e) {
                console.trace(e);
                throw new Error(`Failed to read output file ${path}: ${e}`);
            }
        }
        const result = {
            outputs,
            mergedOutputs,
            exitCode,
            elapsedMillis,
        };
        console.debug(result);
        callback({ result });
    }
    catch (e) {
        const end = performance.now();
        const elapsedMillis = end - start;
        console.trace(e);
        const error = `${e}`;
        mergedOutputs.push({ error });
        callback({
            result: {
                exitCode: undefined,
                error,
                mergedOutputs,
                elapsedMillis,
            }
        });
    }
});
